// API client for the qualification wizard (ticket 130, slice S3).
import { api } from './api';

export const qualificationApi = {
  listPrograms: () => api.get('/qualification/programs').then(r => r.data),
  updateProgram: (id, body) => api.put(`/qualification/programs/${id}`, body).then(r => r.data),
  retireProgram: (id) => api.delete(`/qualification/programs/${id}`).then(r => r.data),
  restoreProgram: (id) => api.post(`/qualification/programs/${id}/restore`).then(r => r.data),
  getDefaults: () => api.get('/qualification/defaults').then(r => r.data),
  updateDefaults: (body) => api.put('/qualification/defaults', body).then(r => r.data),
  calculate: (input) => api.post('/qualification/calculate', input).then(r => r.data),
  listScenarios: (contactId, loanId) =>
    api.get(`/qualification/contacts/${contactId}/scenarios`, { params: loanId ? { loan_id: loanId } : {} }).then(r => r.data),
  createScenario: (contactId, body) => api.post(`/qualification/contacts/${contactId}/scenarios`, body).then(r => r.data),
  createProgramSet: (contactId, body) => api.post(`/qualification/contacts/${contactId}/scenarios/program-set`, body).then(r => r.data),
  updateScenario: (id, body) => api.put(`/qualification/scenarios/${id}`, body).then(r => r.data),
  deleteScenario: (id) => api.delete(`/qualification/scenarios/${id}`).then(r => r.data),
  compare: (ids) => api.get('/qualification/scenarios/compare', { params: { ids: ids.join(',') } }).then(r => r.data),
  listIncomeCalcs: (contactId) => api.get(`/qualification/contacts/${contactId}/income-calcs`).then(r => r.data),
  applyToLoan: (scenarioId, body) => api.post(`/qualification/scenarios/${scenarioId}/apply-to-loan`, body).then(r => r.data),
  createLoan: (scenarioId, body) => api.post(`/qualification/scenarios/${scenarioId}/create-loan`, body).then(r => r.data),
};

export const STAGE_SUGGESTION_LABELS = {
  preapproved: 'Pre-Approved',
  prequalified: 'Pre-Qual',
};

export const PROGRAM_STATUS_LABELS = {
  qualified: 'Qualified',
  conditionally_qualified: 'Conditionally Qualified',
  not_qualified: 'Not Qualified',
};

export const OVERALL_STATUS_LABELS = {
  qualified: 'Qualified',
  conditionally_qualified: 'Conditionally Qualified',
  not_qualified: 'Not Qualified',
  needs_review: 'Needs Review',
};

export const PROPERTY_TYPE_LABELS = {
  single_family: 'Single Family',
  condo: 'Condo',
  townhouse: 'Townhouse',
  multi_unit_2_4: 'Multi-Unit (2-4)',
  manufactured: 'Manufactured',
};

export const PROPERTY_USE_LABELS = {
  primary: 'Primary Residence',
  secondary: 'Second Home',
  investment: 'Investment',
};

export const VETERAN_LABELS = {
  none: 'Not a Veteran',
  veteran: 'Veteran',
  active_duty: 'Active Duty',
  reservist: 'Reservist',
  surviving_spouse: 'Surviving Spouse',
};

export const CREDIT_RANGE_LABELS = {
  below_580: 'Below 580',
  '580_619': '580-619',
  '620_659': '620-659',
  '660_699': '660-699',
  '700_739': '700-739',
  '740_plus': '740+',
};

export const INCOME_TYPE_LABELS = {
  w2: 'W-2',
  self_employed: 'Self-Employed',
  mixed: 'Mixed',
  '1099': '1099',
  retired: 'Retired',
  social_security: 'Social Security',
};

// Backend income_type values (saved Income Calculator rows); not the wizard's own types above.
export const CALC_INCOME_TYPE_LABELS = {
  w2_salary: 'W-2 Salary',
  w2_hourly: 'W-2 Hourly',
  w2_overtime_bonus: 'W-2 Overtime / Bonus',
  self_employed_sch_c: 'Self-Employed (Sch C)',
  k1: 'K-1',
  rental: 'Rental',
  retirement: 'Retirement / SS / Pension',
  bank_statement: 'Bank Statement',
};

export const EMPLOYMENT_LABELS = {
  under_1yr: 'Under 1 Year',
  '1_2yr': '1-2 Years',
  over_2yr: 'Over 2 Years',
};

export const DOWN_SOURCE_LABELS = {
  savings: 'Savings',
  gift: 'Gift',
  grant: 'Grant',
  sale_of_home: 'Sale of Home',
  '401k': '401(k)',
  other: 'Other',
};

export const BANKRUPTCY_LABELS = {
  none: 'None',
  chapter_7_under_2yr: 'Chapter 7, Under 2 Years',
  chapter_7_2_4yr: 'Chapter 7, 2-4 Years',
  chapter_7_over_4yr: 'Chapter 7, Over 4 Years',
  chapter_13_under_2yr: 'Chapter 13, Under 2 Years',
  chapter_13_2_4yr: 'Chapter 13, 2-4 Years',
  chapter_13_over_4yr: 'Chapter 13, Over 4 Years',
};

export const FORECLOSURE_LABELS = {
  none: 'None',
  under_3yr: 'Under 3 Years',
  '3_5yr': '3-5 Years',
  '5_7yr': '5-7 Years',
  over_7yr: 'Over 7 Years',
};

export const EMPTY_INPUT = {
  borrower_type: 'individual',
  first_time_buyer: false,
  veteran_status: 'none',
  property_type: 'single_family',
  property_use: 'primary',
  property_state: 'TX',
  purchase_price: 0,
  credit_score: null,
  credit_score_range: null,
  bankruptcy_history: 'none',
  foreclosure_history: 'none',
  income_source: 'manual',
  income_calc_id: null,
  annual_income: 0,
  program_monthly_income: null,
  employment_length: 'over_2yr',
  income_type: 'w2',
  down_payment_amount: 0,
  down_payment_source: 'savings',
  reserves_months: 0,
  monthly_debt_payments: 0,
  rate_overrides: {},
  property_tax_rate: 1.2,
  property_tax_source: 'tenant_default',
  insurance_rate: 0.35,
  hoa_monthly: 0,
  term_months: 360,
};

export function statusColor(status) {
  switch (status) {
    case 'qualified':
      return 'text-green-700 bg-green-50 border-green-200';
    case 'conditionally_qualified':
      return 'text-amber-700 bg-amber-50 border-amber-200';
    case 'not_qualified':
      return 'text-red-700 bg-red-50 border-red-200';
    case 'needs_review':
      return 'text-slate-600 bg-slate-100 border-slate-200';
    default:
      return 'text-slate-600 bg-slate-50 border-slate-200';
  }
}
